/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { 
  Package, 
  RefreshCw, 
  DollarSign, 
  Plus, 
  Save, 
  Upload, 
  FileSpreadsheet, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  Search, 
  Info,
  CheckCircle2,
  Download,
  Copy,
  Image as ImageIcon,
  Eye,
  ArrowRightLeft,
  FileDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Rotate3d
} from 'lucide-react';
import { GoodsItem, isClientRole, isClientUser, isClientOperatorUser, shouldHidePricesForUser } from '../types.ts';
import { downloadGoodsCatalogPDF } from '../utils/pdfGenerator.ts';
import * as XLSX from 'xlsx';
import ThreeDViewer from './ThreeDViewer.tsx';

// Helper to convert an image URL to Base64 safely
const getBase64Image = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }
    // If it is already a base64 DataURL, resolve immediately
    if (url.startsWith('data:')) {
      resolve(url);
      return;
    }
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/jpeg', 0.85);
          resolve(dataURL);
        } else {
          reject(new Error('Could not get canvas context'));
        }
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (err) => reject(new Error('Failed to load image'));
    img.src = url;
  });
};

// Helper function to render price values up to 30 decimals without floating-point issues
export function formatPriceValue(val: number | string): string {
  if (val === undefined || val === null) return '0.00';
  let strVal = val.toString().trim();
  if (!strVal) return '0.00';
  
  const isNegative = strVal.startsWith('-');
  if (isNegative) {
    strVal = strVal.substring(1);
  }
  
  const parts = strVal.split('.');
  const integerPart = parts[0] || '0';
  const decimalPart = parts[1];
  
  // Format integer part with thousands separators
  const numInt = parseInt(integerPart, 10);
  const formattedInteger = isNaN(numInt) ? integerPart : numInt.toLocaleString('es-MX');
  
  let result = formattedInteger;
  if (decimalPart !== undefined) {
    // If we have decimals, keep up to 30 decimals exactly as is
    result = `${formattedInteger}.${decimalPart}`;
  } else {
    result = `${formattedInteger}.00`;
  }
  
  return isNegative ? `-${result}` : result;
}

interface GoodsCatalogProps {
  goods: GoodsItem[];
  clientId?: string;
  exchangeRate: number;
  lastUpdatedRate: string;
  onRefreshExchangeRate: () => Promise<void>;
  onAddGoodsItem: (item: GoodsItem) => void;
  onUpdateGoodsItem?: (item: GoodsItem) => void;
  onDeleteGoodsItem?: (id: string) => void;
  onDeleteMultipleGoodsItems?: (ids: string[]) => Promise<void>;
  onDeleteAllGoodsItems?: () => Promise<void>;
  currentUser: { role: string };
  isLoading?: boolean;
  isSyncing?: boolean;
}

// Beautiful high-quality industrial presets
const PRESET_IMAGES = [
  {
    id: 'coil',
    name: 'Bobinas de Acero',
    url: 'https://images.unsplash.com/photo-1535813547-99c456a41d4a?auto=format&fit=crop&w=300&q=80'
  },
  {
    id: 'parts',
    name: 'Chasis y Troqueles',
    url: 'https://images.unsplash.com/photo-1537462715879-360eeb61a0bc?auto=format&fit=crop&w=300&q=80'
  },
  {
    id: 'fasteners',
    name: 'Pernos y Tornillería',
    url: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=300&q=80'
  },
  {
    id: 'machined',
    name: 'Maquinados de Precisión',
    url: 'https://images.unsplash.com/photo-1508962914676-134849a727f0?auto=format&fit=crop&w=300&q=80'
  },
  {
    id: 'sheets',
    name: 'Láminas Metálicas',
    url: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=300&q=80'
  }
];

export default function GoodsCatalog({
  goods,
  clientId,
  exchangeRate,
  lastUpdatedRate,
  onRefreshExchangeRate,
  onAddGoodsItem,
  onUpdateGoodsItem,
  onDeleteGoodsItem,
  onDeleteMultipleGoodsItems,
  onDeleteAllGoodsItems,
  currentUser,
  isLoading = false,
  isSyncing = false
}: GoodsCatalogProps) {
  
  const hidePrices = shouldHidePricesForUser(currentUser as any) || isClientOperatorUser(currentUser as any);
  const [isSyncingState, setIsSyncingState] = useState(false);
  const [isRefreshingList, setIsRefreshingList] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isBulkAdding, setIsBulkAdding] = useState(false);

  const handleManualRefreshCatalog = () => {
    setIsRefreshingList(true);
    setTimeout(() => {
      setIsRefreshingList(false);
    }, 600);
  };

  // Multiple item selection
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Sync selectedIds with existing goods to prevent storing orphaned selection IDs
  React.useEffect(() => {
    const existingGoodsIds = new Set(goods.map(g => g.id));
    setSelectedIds(prev => prev.filter(id => existingGoodsIds.has(id)));
  }, [goods]);

  // Search State
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting State
  const [sortBy, setSortBy] = useState<'id' | 'item' | 'price' | 'none'>('none');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Primary input currency preference for adding and viewing: 'MXN' or 'USD'
  const [catalogCurrency, setCatalogCurrency] = useState<'MXN' | 'USD'>('MXN');

  // Form State for Manual Item
  const [gCode, setGCode] = useState('');
  const [gDesc, setGDesc] = useState('');
  const [gSpecs, setGSpecs] = useState('');
  const [gUnit, setGUnit] = useState('PIECE');
  const [gPriceInput, setGPriceInput] = useState(''); // Value in active catalogCurrency
  const [gWeight, setGWeight] = useState('0');
  const [gImageUrl, setGImageUrl] = useState(PRESET_IMAGES[0].url);
  const [gSelectedPresetId, setGSelectedPresetId] = useState('coil');

  // Inline Row Edit State
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editCode, setEditCode] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSpecs, setEditSpecs] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [editPriceInput, setEditPriceInput] = useState(''); // Value in active catalogCurrency
  const [editWeight, setEditWeight] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');

  // Bulk Upload State
  const [csvInput, setCsvInput] = useState('');
  const [previewList, setPreviewList] = useState<any[]>([]);
  const [dragActive, setDragActive] = useState(false);

  // Image zoom modal
  const [zoomedImage, setZoomedImage] = useState<{ url: string; title: string } | null>(null);

  // 3D Visualizer modal
  const [threeDItem, setThreeDItem] = useState<{ imageUrl: string; title: string; code?: string } | null>(null);

  // Download Catalog States
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  // Professional Excel Export
  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    try {
      const items = filteredGoods;
      if (items.length === 0) {
        await window.showAppAlert('No hay mercancías en la lista actual para exportar.', 'Catálogo Vacío');
        setIsExportingExcel(false);
        return;
      }

      // Format data professionally
      const dataToExport = items.map((item, index) => ({
        'Nº': index + 1,
        'CÓDIGO (SKU)': item.code,
        'DESCRIPCIÓN': item.description,
        'FICHA TÉCNICA': item.specifications || '',
        'UNIDAD DE MEDIDA': item.unit,
        'PESO (KG)': item.weightKg || 0,
        'PRECIO UNITARIO (MXN)': item.priceMXN,
        'PRECIO UNITARIO (USD)': item.priceUSD,
        'URL DE FOTOGRAFÍA': item.imageUrl || ''
      }));

      // Create an empty worksheet
      const worksheet = XLSX.utils.json_to_sheet([]);

      // Add a beautifully formatted metadata section at the top
      XLSX.utils.sheet_add_aoa(worksheet, [
        ['GRUPO ISN - LOGÍSTICA & TRASLADOS'],
        ['CATÁLOGO TÉCNICO OFICIAL DE MERCANCÍAS'],
        [`Fecha de Emisión: ${new Date().toLocaleString('es-MX')}`],
        [`Divisa Activa de Operación: ${catalogCurrency}`],
        [`Tipo de Cambio de Referencia: 1 USD = $${exchangeRate.toFixed(4)} MXN`],
        [`Total de Ítems Registrados: ${items.length}`],
        [] // Blank spacer row
      ], { origin: 'A1' });

      // Add data headers and rows starting from row 8
      XLSX.utils.sheet_add_json(worksheet, dataToExport, {
        origin: 'A8',
        skipHeader: false
      });

      // Professional Column Width Adjustments (character count spacing)
      const colWidths = [
        { wch: 6 },   // Nº
        { wch: 18 },  // CÓDIGO
        { wch: 45 },  // DESCRIPCIÓN
        { wch: 18 },  // UNIDAD
        { wch: 12 },  // PESO
        { wch: 22 },  // PRECIO MXN
        { wch: 22 },  // PRECIO USD
        { wch: 50 }   // IMAGE URL
      ];
      worksheet['!cols'] = colWidths;

      // Create workbook and append sheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Catálogo de Mercancías');

      // Export and trigger browser download
      XLSX.writeFile(workbook, `Grupo_ISN_Catalogo_Mercancias_${catalogCurrency}_${new Date().toISOString().split('T')[0]}.xlsx`);
      await window.showAppAlert('Catálogo de mercancías exportado a Excel exitosamente.', 'Descarga Excel Exitosa');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      await window.showAppAlert('Hubo un error al generar el archivo Excel de mercancías.', 'Error Excel');
    } finally {
      setIsExportingExcel(false);
    }
  };

  // Professional PDF Export with embedded photos and Grupo ISN Logo
  const handleExportPDF = async () => {
    setIsExportingPDF(true);
    try {
      const items = filteredGoods;
      if (items.length === 0) {
        await window.showAppAlert('No hay mercancías en la lista actual para exportar.', 'Catálogo Vacío');
        setIsExportingPDF(false);
        return;
      }

      const clientName = clientId ? `CLIENTE-${clientId}` : 'GRUPO ISN';
      downloadGoodsCatalogPDF(items, clientName, { hidePrices });
      await window.showAppAlert('Catálogo de mercancías PDF Oficial generado y descargado exitosamente.', 'Descarga PDF Exitosa');
    } catch (err) {
      console.error('Error generating PDF:', err);
      await window.showAppAlert('Error al generar el catálogo PDF.', 'Error PDF');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleSyncRate = async () => {
    setIsSyncingState(true);
    try {
      await onRefreshExchangeRate();
      await window.showAppAlert(`Tipo de cambio actualizado exitosamente: $${exchangeRate} MXN por 1 USD.`, 'Tipo de Cambio');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSyncingState(false);
    }
  };

  const handleAddGoods = (e: React.FormEvent) => {
    e.preventDefault();
    if (isClientRole(currentUser.role)) return;

    const rawInput = gPriceInput.trim();
    const numVal = parseFloat(rawInput) || 0;
    
    const dotIdx = rawInput.indexOf('.');
    const decCount = dotIdx !== -1 ? (rawInput.length - dotIdx - 1) : 2;
    const precision = Math.max(2, Math.min(30, decCount));

    let priceMXN: string | number = 0;
    let priceUSD: string | number = 0;

    if (catalogCurrency === 'MXN') {
      priceMXN = rawInput;
      const usd = numVal / exchangeRate;
      priceUSD = usd.toFixed(precision).replace(/\.?0+$/, '');
      if (!priceUSD || priceUSD === '.') priceUSD = '0';
    } else {
      priceUSD = rawInput;
      const mxn = numVal * exchangeRate;
      priceMXN = mxn.toFixed(precision).replace(/\.?0+$/, '');
      if (!priceMXN || priceMXN === '.') priceMXN = '0';
    }

    const weightKg = parseFloat(gWeight) || 0;

    const newItem: GoodsItem = {
      id: `cat_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      clientId,
      code: gCode.trim().toUpperCase(),
      description: gDesc.trim(),
      specifications: gSpecs.trim(),
      unit: gUnit.toUpperCase(),
      priceMXN,
      priceUSD,
      weightKg,
      imageUrl: gImageUrl
    };

    onAddGoodsItem(newItem);
    
    // Reset Form
    setGCode('');
    setGDesc('');
    setGSpecs('');
    setGUnit('PIECE');
    setGPriceInput('');
    setGWeight('0');
    setGImageUrl(PRESET_IMAGES[0].url);
    setGSelectedPresetId('coil');
    setIsAdding(false);
  };

  // Inline row edit actions
  const startEditItem = (item: GoodsItem) => {
    setEditingItemId(item.id);
    setEditCode(item.code);
    setEditDesc(item.description);
    setEditSpecs(item.specifications || '');
    setEditUnit(item.unit);
    
    // Set edit price according to selected display currency
    const priceVal = catalogCurrency === 'MXN' ? item.priceMXN : item.priceUSD;
    setEditPriceInput(priceVal.toString());
    setEditWeight((item.weightKg || 0).toString());
    setEditImageUrl(item.imageUrl || PRESET_IMAGES[0].url);
  };

  const cancelEditItem = () => {
    setEditingItemId(null);
  };

  const saveEditItem = (id: string) => {
    if (!onUpdateGoodsItem) return;

    const rawInput = editPriceInput.trim();
    const numVal = parseFloat(rawInput) || 0;

    const dotIdx = rawInput.indexOf('.');
    const decCount = dotIdx !== -1 ? (rawInput.length - dotIdx - 1) : 2;
    const precision = Math.max(2, Math.min(30, decCount));

    let priceMXN: string | number = 0;
    let priceUSD: string | number = 0;

    if (catalogCurrency === 'MXN') {
      priceMXN = rawInput;
      const usd = numVal / exchangeRate;
      priceUSD = usd.toFixed(precision).replace(/\.?0+$/, '');
      if (!priceUSD || priceUSD === '.') priceUSD = '0';
    } else {
      priceUSD = rawInput;
      const mxn = numVal * exchangeRate;
      priceMXN = mxn.toFixed(precision).replace(/\.?0+$/, '');
      if (!priceMXN || priceMXN === '.') priceMXN = '0';
    }

    const weightKg = parseFloat(editWeight) || 0;

    const existingItem = goods.find(g => g.id === id);
    const itemClientId = existingItem?.clientId || clientId;

    const updatedItem: GoodsItem = {
      id,
      clientId: itemClientId,
      code: editCode.trim().toUpperCase(),
      description: editDesc.trim(),
      specifications: editSpecs.trim(),
      unit: editUnit.toUpperCase(),
      priceMXN,
      priceUSD,
      weightKg,
      imageUrl: editImageUrl
    };

    onUpdateGoodsItem(updatedItem);
    setEditingItemId(null);
  };

  const deleteItem = async (id: string) => {
    if (!onDeleteGoodsItem) return;
    const isConfirmed = await window.showAppConfirm('¿Está seguro de eliminar esta mercancía del catálogo?', 'Eliminar Mercancía');
    if (isConfirmed) {
      onDeleteGoodsItem(id);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!onDeleteMultipleGoodsItems && !onDeleteGoodsItem) return;

    const isConfirmed = await window.showAppConfirm(
      `¿Está seguro de eliminar las ${selectedIds.length} mercancías seleccionadas del catálogo?`, 
      'Eliminar Selección'
    );
    if (isConfirmed) {
      try {
        if (onDeleteMultipleGoodsItems) {
          await onDeleteMultipleGoodsItems(selectedIds);
        } else if (onDeleteGoodsItem) {
          // Fallback sequential deletion
          for (const id of selectedIds) {
            onDeleteGoodsItem(id);
          }
        }
        setSelectedIds([]);
        await window.showAppAlert('Las mercancías seleccionadas han sido eliminadas.', 'Eliminación Exitosa');
      } catch (err) {
        console.error("Error deleting selected items:", err);
      }
    }
  };

  const handleDeleteAll = async () => {
    if (goods.length === 0) return;
    if (!onDeleteAllGoodsItems && !onDeleteGoodsItem) return;

    const isConfirmed = await window.showAppConfirm(
      '⚠️ ADVERTENCIA: ¿Está seguro de eliminar POR COMPLETO todas las mercancías del catálogo? Esta acción no se puede deshacer.', 
      'Eliminar Todo el Catálogo'
    );
    if (isConfirmed) {
      try {
        if (onDeleteAllGoodsItems) {
          await onDeleteAllGoodsItems();
        } else if (onDeleteGoodsItem) {
          // Fallback sequential deletion
          const ids = goods.map(g => g.id);
          for (const id of ids) {
            onDeleteGoodsItem(id);
          }
        }
        setSelectedIds([]);
        await window.showAppAlert('Todas las mercancías del catálogo han sido eliminadas.', 'Catálogo Vacío');
      } catch (err) {
        console.error("Error deleting all items:", err);
      }
    }
  };

  // Image selection for custom manual upload (Base64 file reader)
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, isEditMode = false) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (uploadEvent) => {
        const base64 = uploadEvent.target?.result as string;
        if (base64) {
          if (isEditMode) {
            setEditImageUrl(base64);
          } else {
            setGImageUrl(base64);
            setGSelectedPresetId('custom');
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // CSV Parsing Logic
  const parseCSV = (text: string) => {
    const lines = text.split('\n');
    const results: any[] = [];
    
    if (lines.length === 0) return results;
    
    let startIndex = 0;
    
    // Check if first line contains header titles
    const firstLine = lines[0].toLowerCase();
    if (
      firstLine.includes('item') || 
      firstLine.includes('desc') || 
      firstLine.includes('code') || 
      firstLine.includes('precio') || 
      firstLine.includes('unit') || 
      firstLine.includes('id')
    ) {
      startIndex = 1;
    }
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Split by comma or semicolon
      const cols = line.split(/[;,]/).map(c => c.trim().replace(/^["']|["']$/g, ''));
      if (cols.length < 2) continue; // Skip empty/invalid lines
      
      const code = cols[0] || `cat_bulk_${Math.random().toString(36).substr(2, 5)}`;
      const description = cols[1] || 'Sin descripción';
      const unit = (cols[2] || 'PIECE').toUpperCase();
      const priceRawStr = cols[3] || '0';
      const priceVal = parseFloat(priceRawStr) || 0.0;
      const weightKg = parseFloat(cols[4]) || 0.0;
      const imageUrl = cols[5] || PRESET_IMAGES[Math.floor(Math.random() * PRESET_IMAGES.length)].url;
      
      const dotIdx = priceRawStr.indexOf('.');
      const decCount = dotIdx !== -1 ? (priceRawStr.length - dotIdx - 1) : 2;
      const precision = Math.max(2, Math.min(30, decCount));

      let priceMXN: string | number = 0;
      let priceUSD: string | number = 0;

      // Handle raw parsed prices based on currently selected active upload currency
      if (catalogCurrency === 'MXN') {
        priceMXN = priceRawStr;
        const usd = priceVal / exchangeRate;
        priceUSD = usd.toFixed(precision).replace(/\.?0+$/, '');
        if (!priceUSD || priceUSD === '.') priceUSD = '0';
      } else {
        priceUSD = priceRawStr;
        const mxn = priceVal * exchangeRate;
        priceMXN = mxn.toFixed(precision).replace(/\.?0+$/, '');
        if (!priceMXN || priceMXN === '.') priceMXN = '0';
      }
      
      results.push({
        id: `cat_${Date.now()}_${Math.random().toString(36).substr(2, 5)}_${i}`,
        clientId,
        code: code.trim().toUpperCase(),
        description: description.trim(),
        unit,
        priceMXN,
        priceUSD,
        weightKg,
        imageUrl
      });
    }
    
    return results;
  };

  // Drag and Drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) {
        setCsvInput(text);
        const parsed = parseCSV(text);
        setPreviewList(parsed);
      }
    };
    reader.readAsText(file);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setCsvInput(text);
    const parsed = parseCSV(text);
    setPreviewList(parsed);
  };

  const handleConfirmBulkUpload = async () => {
    if (previewList.length === 0) {
      await window.showAppAlert('No hay mercancías válidas para cargar.', 'Carga Masiva Vacía');
      return;
    }
    
    previewList.forEach(item => {
      onAddGoodsItem({
        ...item,
        clientId: item.clientId || clientId
      });
    });

    await window.showAppAlert(`Se cargaron exitosamente ${previewList.length} mercancías al catálogo.`, 'Carga Masiva Exitosa');
    setCsvInput('');
    setPreviewList([]);
    setIsBulkAdding(false);
  };

  const handleClearBulk = () => {
    setCsvInput('');
    setPreviewList([]);
  };

  // Download official CSV template
  const handleDownloadTemplate = () => {
    const headers = 'CÓDIGO,DESCRIPCIÓN,UNIDAD,PRECIO,PESO_KG,IMAGE_URL\n';
    const row1 = `COIL-01,Bobina de Acero Calibre 14,PIECE,12500.00,450.00,${PRESET_IMAGES[0].url}\n`;
    const row2 = `BOLT-HEX,Perno Hexagonal de Alta Resistencia,KG,85.50,1.00,${PRESET_IMAGES[2].url}\n`;
    const row3 = `TUBE-GALV,Perfil Tubular Galvanizado,MTR,320.00,12.50,${PRESET_IMAGES[3].url}\n`;
    
    const csvContent = headers + row1 + row2 + row3;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `plantilla_carga_masiva_catalogo_${catalogCurrency.toLowerCase()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Copy template text to clipboard
  const handleCopyTemplate = async () => {
    const csvSample = `CÓDIGO,DESCRIPCIÓN,UNIDAD,PRECIO,PESO_KG,IMAGE_URL
COIL-01,Bobina de Acero Calibre 14,PIECE,12500.00,450.00,${PRESET_IMAGES[0].url}
BOLT-HEX,Perno Hexagonal de Alta Resistencia,KG,85.50,1.00,${PRESET_IMAGES[2].url}`;
    
    navigator.clipboard.writeText(csvSample);
    await window.showAppAlert('Plantilla copiada al portapapeles exitosamente.', 'Copiado Exitosamente');
  };

  const handleSort = (field: 'id' | 'item' | 'price') => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Filter goods based on search input
  const filteredGoods = goods.filter(item => {
    const term = searchTerm.toLowerCase();
    const itemCode = item.code?.toLowerCase() || '';
    const itemDesc = item.description?.toLowerCase() || '';
    const itemUnit = item.unit?.toLowerCase() || '';
    return itemCode.includes(term) || itemDesc.includes(term) || itemUnit.includes(term);
  });

  // Sort filtered goods
  if (sortBy !== 'none') {
    filteredGoods.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'id') {
        const aCode = a.code || '';
        const bCode = b.code || '';
        comparison = aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: 'base' });
      } else if (sortBy === 'item') {
        const aDesc = a.description || '';
        const bDesc = b.description || '';
        comparison = aDesc.localeCompare(bDesc, undefined, { sensitivity: 'base' });
      } else if (sortBy === 'price') {
        const aPrice = parseFloat((catalogCurrency === 'MXN' ? a.priceMXN : a.priceUSD).toString()) || 0;
        const bPrice = parseFloat((catalogCurrency === 'MXN' ? b.priceMXN : b.priceUSD).toString()) || 0;
        comparison = aPrice - bPrice;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }

  return (
    <div className="space-y-6" id="goods-catalogue-container">
      
      {/* Exchange Rate Bar and Dual Currency Preference Configuration */}
      {!hidePrices && (
        <div className="bg-slate-900 text-white p-5 rounded-2xl border border-slate-800 shadow-md flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          
          {/* FX Information */}
          <div className="space-y-1">
            <span className="text-[10px] uppercase font-bold text-emerald-400 tracking-widest font-mono flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Tipo de Cambio Sincronizado
            </span>
            <h3 className="text-xl font-black tracking-tight flex items-center gap-2">
              1 USD = <span className="text-yellow-400 font-mono">${exchangeRate.toFixed(4)}</span> MXN
            </h3>
            <p className="text-xs text-slate-400 font-medium">
              Última actualización: {new Date(lastUpdatedRate).toLocaleString('es-MX')}
            </p>
          </div>

          {/* Dynamic Currency selection matches requested "se pueda seleccionar la divisa con la cual se va a manejar el catálogo" */}
          <div className="bg-slate-800/80 p-3.5 rounded-xl border border-slate-700/60 flex flex-col sm:flex-row items-start sm:items-center gap-3.5">
            <div className="space-y-0.5">
              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block font-mono">Divisa de Operación del Catálogo</span>
              <p className="text-xs text-white font-medium">Define la moneda de entrada y visualización primaria:</p>
            </div>

            <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800 shrink-0">
              <button
                onClick={() => {
                  setCatalogCurrency('MXN');
                  // If editing, close edit to avoid state mismatches
                  setEditingItemId(null);
                }}
                className={`px-3 py-1 rounded-md text-xs font-black transition-all cursor-pointer flex items-center gap-1 ${
                  catalogCurrency === 'MXN'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                MXN ($)
              </button>
              <button
                onClick={() => {
                  setCatalogCurrency('USD');
                  // If editing, close edit to avoid state mismatches
                  setEditingItemId(null);
                }}
                className={`px-3 py-1 rounded-md text-xs font-black transition-all cursor-pointer flex items-center gap-1 ${
                  catalogCurrency === 'USD'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                USD ($)
              </button>
            </div>
          </div>

          <button
            onClick={handleSyncRate}
            disabled={isSyncing}
            className="bg-slate-800 hover:bg-slate-700 active:bg-slate-750 text-white border border-slate-700 px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 self-start lg:self-auto disabled:opacity-50 shrink-0 cursor-pointer"
          >
            <RefreshCw className={`h-4 w-4 text-emerald-400 ${isSyncing ? 'animate-spin' : ''}`} />
            Sincronizar Divisas
          </button>
        </div>
      )}

      {/* CARGA MASIVA PANEL (CSV) with TEMPLATE options */}
      {isBulkAdding && !isClientRole(currentUser.role) && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-md space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
              <div>
                <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight">Carga Masiva de Mercancías</h4>
                <p className="text-[10px] text-slate-500 font-medium">Asociando precios en <span className="font-bold text-emerald-600 underline">{catalogCurrency}</span> según configuración activa.</p>
              </div>
            </div>
            <button 
              onClick={() => {
                setIsBulkAdding(false);
                handleClearBulk();
              }} 
              className="text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* CSV Import Methods & Templates */}
            <div className="space-y-4">
              
              {/* Plantilla de Carga Masiva - Matches "tenga una plantilla para la carga masiva" */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider font-mono flex items-center gap-1">
                    <Info className="h-3.5 w-3.5 text-blue-500" />
                    Estructura de la Plantilla ({catalogCurrency})
                  </span>
                  
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleDownloadTemplate}
                      className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-bold border border-emerald-200 flex items-center gap-1 transition-all cursor-pointer"
                      title="Descargar Plantilla CSV Oficial"
                    >
                      <Download className="h-3 w-3" /> Descargar CSV
                    </button>
                    <button
                      onClick={handleCopyTemplate}
                      className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-750 rounded-lg text-[10px] font-bold border border-slate-200 flex items-center gap-1 transition-all cursor-pointer"
                      title="Copiar texto de ejemplo"
                    >
                      <Copy className="h-3 w-3" /> Copiar Ejemplo
                    </button>
                  </div>
                </div>

                <div className="space-y-1 text-[11px] text-slate-600 leading-relaxed">
                  <p>Columnas requeridas separadas por comas:</p>
                  <code className="block bg-slate-950 p-2.5 rounded-lg text-[10px] font-mono text-slate-200 select-all overflow-x-auto whitespace-nowrap border border-slate-800">
                    CÓDIGO, DESCRIPCIÓN, UNIDAD, PRECIO_{catalogCurrency}, PESO_KG, IMAGE_URL
                  </code>
                  <p className="text-[10px] text-slate-400 mt-1">
                    * Si dejas vacía la columna <code className="font-mono bg-slate-200 text-slate-700 px-0.5 rounded">IMAGE_URL</code>, el sistema le asignará una imagen industrial de forma aleatoria para mantener la estética visual.
                  </p>
                </div>
              </div>

              {/* Drag & Drop Area */}
              <div 
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all flex flex-col items-center justify-center cursor-pointer ${
                  dragActive ? 'border-emerald-500 bg-emerald-50/40' : 'border-slate-200 bg-slate-50/50 hover:bg-slate-50'
                }`}
                onClick={() => document.getElementById('bulk-file-input')?.click()}
              >
                <Upload className="h-8 w-8 text-emerald-600 mb-2 animate-bounce-slow" />
                <p className="text-xs font-bold text-slate-700">Arrastre su archivo CSV cargado con la plantilla</p>
                <p className="text-[10px] text-slate-400 mt-1">o haga clic para seleccionar desde su equipo</p>
                <input 
                  id="bulk-file-input"
                  type="file" 
                  accept=".csv" 
                  className="hidden" 
                  onChange={handleFileChange}
                />
              </div>

              {/* Manual Raw Paste Area */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block">O pegue el contenido CSV directamente:</label>
                <textarea
                  rows={4}
                  value={csvInput}
                  onChange={handleTextInputChange}
                  placeholder={`CÓDIGO,DESCRIPCIÓN,UNIDAD,PRECIO_${catalogCurrency},PESO_KG,IMAGE_URL&#10;PROD-01,Bobina Galvanizada,PIECE,15400.50,340,https://images.unsplash.com/...`}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-mono focus:outline-hidden focus:border-emerald-500"
                />
              </div>
            </div>

            {/* CSV Parser Preview List */}
            <div className="space-y-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Vista Previa de la Lista ({previewList.length} ítems)</h5>
                  {previewList.length > 0 && (
                    <button 
                      onClick={handleClearBulk}
                      className="text-[10px] font-bold text-red-600 hover:underline cursor-pointer"
                    >
                      Limpiar todo
                    </button>
                  )}
                </div>

                {previewList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 text-slate-400 space-y-2 border border-slate-100 rounded-xl mt-3 bg-slate-50/20">
                    <FileSpreadsheet className="h-8 w-8 text-slate-300" />
                    <p className="text-xs text-slate-500 font-medium">Ningún archivo cargado o texto pegado aún</p>
                  </div>
                ) : (
                  // Scrollbar added to prevent long list heights
                  <div className="max-h-[250px] overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-xl mt-3 bg-white pr-1">
                    {previewList.map((item, idx) => (
                      <div key={idx} className="p-2.5 flex items-center justify-between text-xs gap-3">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          {/* Image preview in parser */}
                          <img 
                            src={item.imageUrl} 
                            alt={item.description} 
                            className="w-8 h-8 rounded-lg object-cover border border-slate-200 shrink-0"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = PRESET_IMAGES[0].url;
                            }}
                          />
                          <div className="space-y-0.5 min-w-0">
                            <span className="text-[8px] font-mono bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold uppercase">
                              {item.code}
                            </span>
                            <p className="font-bold text-slate-800 truncate">{item.description}</p>
                            <p className="text-[9px] text-slate-400 font-mono">
                              Unidad: {item.unit} • Peso: {item.weightKg} Kg
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-emerald-600">
                            {catalogCurrency === 'MXN' ? `$${formatPriceValue(item.priceMXN)} MXN` : `$${formatPriceValue(item.priceUSD)} USD`}
                          </p>
                          <p className="text-[9px] text-slate-400 font-mono">
                            {catalogCurrency === 'MXN' ? `$${formatPriceValue(item.priceUSD)} USD` : `$${formatPriceValue(item.priceMXN)} MXN`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {previewList.length > 0 && (
                <div className="pt-4 border-t border-slate-100 flex gap-2">
                  <button
                    onClick={handleConfirmBulkUpload}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <CheckCircle2 className="h-4 w-4" /> Confirmar y Guardar ({previewList.length}) en Catálogo
                  </button>
                  <button
                    onClick={() => {
                      setIsBulkAdding(false);
                      handleClearBulk();
                    }}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Catalog Table Area */}
        <div className="lg:col-span-2 space-y-4">
          
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-100 shadow-xs">
            
            {/* Dynamic Search & Sort Controls */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar mercancías..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50/80 border border-slate-200 rounded-xl pl-9 pr-3 py-1.5 text-xs focus:outline-hidden focus:border-slate-400 text-slate-800"
                />
              </div>

              {/* Quick Sort Dropdown */}
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200/80 rounded-xl px-2 py-1 shrink-0 self-start sm:self-auto">
                <span className="text-[10px] text-slate-400 font-bold font-mono uppercase tracking-wider">Ordenar:</span>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    const val = e.target.value as any;
                    setSortBy(val);
                    if (val !== 'none' && sortOrder === 'asc') {
                      // default to ascending or keep current
                    }
                  }}
                  className="bg-transparent border-0 text-slate-700 text-[10px] font-bold py-0 focus:outline-hidden cursor-pointer"
                >
                  <option value="none">Por Defecto</option>
                  <option value="id">ID / Código</option>
                  <option value="item">Item / Descripción</option>
                  <option value="price">Precio ({catalogCurrency})</option>
                </select>

                {sortBy !== 'none' && (
                  <button
                    type="button"
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="p-1 hover:bg-slate-200/50 rounded-lg text-cyan-600 transition-colors cursor-pointer"
                    title={sortOrder === 'asc' ? 'Orden Ascendente (Click para Descendente)' : 'Orden Descendente (Click para Ascendente)'}
                  >
                    {sortOrder === 'asc' ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 self-stretch xl:self-auto flex-wrap justify-end">
              <span className="text-[10px] font-mono text-slate-400 mr-2 shrink-0">
                Registrados: <strong className="font-bold text-slate-700">{filteredGoods.length} mercancías</strong>
              </span>

              {/* Refresh catalog button */}
              <button
                type="button"
                onClick={handleManualRefreshCatalog}
                className={`bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 p-1.5 rounded-xl text-[10px] font-bold transition-all flex items-center justify-center cursor-pointer ${isRefreshingList ? 'animate-spin text-cyan-600' : ''}`}
                title="Sincronizar y actualizar lista de mercancías"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>

              {/* General Export Buttons available for all users */}
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={isExportingExcel}
                className="bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer disabled:opacity-50"
                title="Descargar catálogo en formato Excel (.xlsx)"
              >
                <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
                {isExportingExcel ? 'Exportando...' : 'Descargar Excel'}
              </button>

              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExportingPDF}
                className="bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer disabled:opacity-50"
                title="Descargar catálogo oficial en PDF con fotografías de las piezas"
              >
                <FileDown className="h-3.5 w-3.5 text-rose-600" />
                {isExportingPDF ? 'Generando PDF...' : 'Descargar PDF (Con Fotos)'}
              </button>

              {isClientUser(currentUser) && (
                <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5 text-slate-500" />
                  <span>Modo Consulta Catálogo</span>
                </span>
              )}

              {!isClientUser(currentUser) && (
                <>
                  {/* Delete Selected Button */}
                  {selectedIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleDeleteSelected}
                      className="bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer"
                      title="Eliminar las mercancías seleccionadas del catálogo"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-rose-500 animate-pulse" />
                      Eliminar Seleccionados ({selectedIds.length})
                    </button>
                  )}

                  {/* Delete All Button */}
                  {goods.length > 0 && (
                    <button
                      type="button"
                      onClick={handleDeleteAll}
                      className="bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer"
                      title="Eliminar por completo todas las mercancías del catálogo"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      Eliminar Todo
                    </button>
                  )}

                  {/* Carga Masiva (CSV) button matches user's screenshot */}
                  <button
                    onClick={() => {
                      setIsBulkAdding(!isBulkAdding);
                      setIsAdding(false);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer ${
                      isBulkAdding ? 'bg-emerald-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5" /> Carga Masiva (CSV)
                  </button>

                  <button
                    onClick={() => {
                      setIsAdding(!isAdding);
                      setIsBulkAdding(false);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer ${
                      isAdding ? 'bg-slate-850 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    <Plus className="h-3.5 w-3.5" /> Agregar Mercancía
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Detailed Sync and Data Source Banner */}
          <div className="flex flex-wrap items-center justify-between bg-slate-50 p-2.5 rounded-xl border border-slate-200 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-slate-700 font-mono text-[11px] font-semibold">
                Origen: Firestore DB / Caché Local Sincronizada
              </span>
            </div>
            {(isLoading || isSyncing || isRefreshingList) && (
              <div className="flex items-center gap-1.5 text-cyan-700 text-[11px] font-bold animate-pulse">
                <RefreshCw className="h-3 w-3 animate-spin" />
                <span>Recuperando y Sincronizando Mercancías...</span>
              </div>
            )}
          </div>

          {/* TABLE CONTAINER WITH MANDATORY MAXIMUM HEIGHT SCROLLBAR to prevent going too far down */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
            
            {/* Header matches professional look with weightKg */}
            <div className="bg-slate-50/80 px-4 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest grid grid-cols-12 gap-2 border-b border-slate-100 font-mono items-center">
              <div className="col-span-1 flex items-center justify-center gap-1.5">
                {!isClientRole(currentUser.role) && filteredGoods.length > 0 && (
                  <input
                    type="checkbox"
                    checked={filteredGoods.length > 0 && filteredGoods.every(item => selectedIds.includes(item.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allFilteredIds = filteredGoods.map(item => item.id);
                        setSelectedIds(prev => Array.from(new Set([...prev, ...allFilteredIds])));
                      } else {
                        const allFilteredIds = filteredGoods.map(item => item.id);
                        setSelectedIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
                      }
                    }}
                    className="accent-cyan-600 rounded cursor-pointer h-3.5 w-3.5"
                    title="Seleccionar todas las mercancías visibles"
                  />
                )}
                <span>Foto</span>
              </div>

              {/* Clickable ID Header */}
              <button
                type="button"
                onClick={() => handleSort('id')}
                className="col-span-2 text-left flex items-center gap-1 hover:bg-slate-100/60 p-1 -m-1 rounded transition-all group font-black text-slate-500 uppercase tracking-widest font-mono cursor-pointer"
                title="Ordenar por ID / Código de mercancía"
              >
                <span>ID / Código</span>
                {sortBy === 'id' ? (
                  sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-cyan-600" /> : <ArrowDown className="h-3 w-3 text-cyan-600" />
                ) : (
                  <ArrowUpDown className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </button>

              {/* Clickable Description Header */}
              <button
                type="button"
                onClick={() => handleSort('item')}
                className="col-span-3 text-left flex items-center gap-1 hover:bg-slate-100/60 p-1 -m-1 rounded transition-all group font-black text-slate-500 uppercase tracking-widest font-mono cursor-pointer"
                title="Ordenar por Descripción / Item"
              >
                <span>Item / Descripción</span>
                {sortBy === 'item' ? (
                  sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-cyan-600" /> : <ArrowDown className="h-3 w-3 text-cyan-600" />
                ) : (
                  <ArrowUpDown className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </button>

              <span className="col-span-1.5 text-center">Unidad</span>

              {/* Clickable Price Header or Operational Header */}
              {hidePrices ? (
                <span className="col-span-2 text-right font-black text-slate-500 uppercase tracking-widest font-mono">
                  Control / Piezas
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSort('price')}
                  className="col-span-2 text-right flex items-center justify-end gap-1 hover:bg-slate-100/60 p-1 -m-1 rounded transition-all group font-black text-slate-500 uppercase tracking-widest font-mono cursor-pointer"
                  title={`Ordenar por Precio (${catalogCurrency})`}
                >
                  <span>Precio ({catalogCurrency})</span>
                  {sortBy === 'price' ? (
                    sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-cyan-600" /> : <ArrowDown className="h-3 w-3 text-cyan-600" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
              )}

              <span className="col-span-1 text-center">kGs</span>
              <span className="col-span-1.5 text-right">Gestión</span>
            </div>

            {/* Scrollable grid area with scrollbar-thin to prevent extending infinitely down */}
            <div className="max-h-[550px] overflow-y-auto divide-y divide-slate-100 pr-1">
              
              {(isLoading || isSyncing || isRefreshingList) ? (
                /* SKELETON SCREEN FOR GOODS CATALOG TABLE */
                <div className="space-y-2 p-3 animate-pulse">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div key={n} className="px-4 py-3.5 bg-slate-50/70 rounded-xl border border-slate-200/80 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-slate-200 rounded-lg shrink-0"></div>
                        <div className="space-y-2 flex-1">
                          <div className="h-3.5 bg-slate-200 rounded-md w-1/4"></div>
                          <div className="h-3 bg-slate-150 rounded-md w-1/2"></div>
                        </div>
                      </div>
                      <div className="h-4 bg-slate-200 rounded-md w-24"></div>
                      <div className="h-4 bg-slate-200 rounded-md w-16"></div>
                    </div>
                  ))}
                </div>
              ) : filteredGoods.length === 0 ? (
                <div className="p-16 text-center text-slate-400 space-y-3">
                  <Package className="h-10 w-10 text-slate-300 mx-auto" />
                  <p className="text-xs font-semibold uppercase text-slate-500 tracking-wider">Ninguna mercancía coincide con la búsqueda</p>
                  <p className="text-[10px] text-slate-400">Prueba cambiando el término de búsqueda o registra un nuevo material.</p>
                </div>
              ) : (
                filteredGoods.map(item => {
                  const isEditing = editingItemId === item.id;
                  const currentImage = item.imageUrl || PRESET_IMAGES[0].url;
                  
                  return (
                    <div key={item.id} className="px-4 py-3.5 grid grid-cols-12 gap-2 items-center text-xs hover:bg-slate-50/40 transition-colors">
                      
                      {/* 0. PHOTO PREVIEW COLUMN - Matches "opción de subir una foto del material con vista de la imagen" */}
                      <div className="col-span-1 flex items-center justify-center gap-1.5">
                        {!isClientRole(currentUser.role) && (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(prev => [...prev, item.id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== item.id));
                              }
                            }}
                            className="accent-cyan-600 rounded cursor-pointer h-3.5 w-3.5 shrink-0"
                          />
                        )}
                        {isEditing ? (
                          <div className="relative cursor-pointer group" title="Haga clic para cambiar o subir imagen">
                            <img 
                              src={editImageUrl} 
                              alt="Material" 
                              className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = PRESET_IMAGES[0].url;
                              }}
                            />
                            <label className="absolute inset-0 bg-slate-950/50 flex flex-col items-center justify-center rounded-lg cursor-pointer hover:bg-slate-950/70 transition-colors">
                              <Upload className="h-3 w-3 text-white" />
                              <span className="text-[7px] text-white font-bold uppercase tracking-tighter mt-0.5">Subir</span>
                              <input 
                                type="file" 
                                accept="image/*" 
                                className="hidden" 
                                onChange={(e) => handleImageUpload(e, true)}
                              />
                            </label>
                          </div>
                        ) : (
                          <div 
                            className="relative group shrink-0"
                            title="Ver opciones de imagen"
                          >
                            <img 
                              src={currentImage} 
                              alt={item.description} 
                              className="w-10 h-10 rounded-lg object-cover border border-slate-200/80 shadow-xs transition-transform duration-250 group-hover:scale-110"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = PRESET_IMAGES[0].url;
                              }}
                            />
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-opacity gap-1.5 px-0.5">
                              <button
                                onClick={() => setZoomedImage({ url: currentImage, title: `${item.description} (${item.code})` })}
                                className="p-1 hover:bg-white/20 rounded-md transition-colors cursor-pointer"
                                title="Zoom 2D"
                              >
                                <Eye className="h-3 w-3 text-white" />
                              </button>
                              <button
                                onClick={() => setThreeDItem({ imageUrl: currentImage, title: item.description, code: item.code })}
                                className="p-1 hover:bg-white/20 rounded-md transition-colors cursor-pointer"
                                title="Girar en 3D"
                              >
                                <Rotate3d className="h-3 w-3 text-cyan-400 animate-pulse" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
 
                      {/* 1. ID / CODE */}
                      <div className="col-span-2 pr-1">
                        {isEditing ? (
                          <input 
                            type="text" 
                            value={editCode} 
                            onChange={(e) => setEditCode(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 font-mono text-[9px] text-slate-800 uppercase"
                          />
                        ) : (
                          <span className="font-mono text-[8px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm uppercase tracking-wide block truncate w-fit font-bold" title={item.code}>
                            {item.code}
                          </span>
                        )}
                      </div>
 
                      {/* 2. ITEM / DESCRIPTION */}
                      <div className="col-span-3 pr-2">
                        {isEditing ? (
                          <div className="flex flex-col gap-2">
                            <div>
                              <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Nombre / Descripción:</span>
                              <input 
                                type="text" 
                                value={editDesc} 
                                onChange={(e) => setEditDesc(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-[10px]"
                              />
                            </div>
                            <div>
                              <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Ficha Técnica:</span>
                              <textarea
                                value={editSpecs}
                                onChange={(e) => setEditSpecs(e.target.value)}
                                placeholder="Normas, dimensiones, grados..."
                                className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-[10px] min-h-[40px] resize-y"
                              />
                            </div>
                            <label className="inline-flex items-center gap-1 text-[9px] text-cyan-600 hover:text-cyan-700 font-bold hover:underline cursor-pointer select-none">
                              <ImageIcon className="h-3 w-3" />
                              <span>Cambiar foto...</span>
                              <input 
                                type="file" 
                                accept="image/*" 
                                className="hidden" 
                                onChange={(e) => handleImageUpload(e, true)}
                              />
                            </label>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <p className="font-bold text-slate-800 line-clamp-2 leading-tight" title={item.description}>
                              {item.description}
                            </p>
                            {item.specifications && (
                              <p className="text-[10px] text-slate-500 leading-normal bg-slate-50/50 p-1 rounded-sm border border-slate-100/60 font-medium whitespace-pre-line">
                                <strong className="text-slate-600 font-bold">Ficha Técnica:</strong> {item.specifications}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 3. UNIT */}
                      <div className="col-span-1.5 text-center">
                        {isEditing ? (
                          <input 
                            type="text" 
                            value={editUnit} 
                            onChange={(e) => setEditUnit(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded px-1 py-0.5 text-[10px] text-center"
                          />
                        ) : (
                          <span className="inline-block text-[10px] font-black text-slate-500 bg-slate-100/60 px-2 py-0.5 rounded-md">
                            {item.unit}
                          </span>
                        )}
                      </div>

                      {/* 4. PRICE UNIT (MXN & USD dynamically adjusted based on selector preference) */}
                      <div className="col-span-2 text-right">
                        {hidePrices ? (
                          <span className="inline-block text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md font-mono">
                            ✓ Registrado
                          </span>
                        ) : isEditing ? (
                          <div className="relative">
                            <input 
                              type="number" 
                              step="any"
                              min="0"
                              value={editPriceInput} 
                              onChange={(e) => setEditPriceInput(e.target.value)}
                              className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-[10px] text-right font-bold text-emerald-600 font-mono"
                            />
                            <span className="absolute left-1 top-1.5 text-[9px] text-slate-400 font-bold">{catalogCurrency}</span>
                          </div>
                        ) : (
                          <div className="space-y-0.5">
                            {catalogCurrency === 'MXN' ? (
                              <>
                                <p className="font-black text-emerald-600 font-mono">${formatPriceValue(item.priceMXN)} MXN</p>
                                <p className="text-[9px] text-slate-400 font-mono">${formatPriceValue(item.priceUSD)} USD</p>
                              </>
                            ) : (
                              <>
                                <p className="font-black text-blue-600 font-mono">${formatPriceValue(item.priceUSD)} USD</p>
                                <p className="text-[9px] text-slate-400 font-mono">${formatPriceValue(item.priceMXN)} MXN</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 5. KGS */}
                      <div className="col-span-1 text-center font-mono font-medium text-slate-600">
                        {isEditing ? (
                          <input 
                            type="number" 
                            step="0.01"
                            value={editWeight} 
                            onChange={(e) => setEditWeight(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded px-1 py-0.5 text-[10px] text-center"
                          />
                        ) : (
                          <span>{(item.weightKg !== undefined ? item.weightKg : 0.0).toFixed(2)}</span>
                        )}
                      </div>

                      {/* 6. ACTION GESTIÓN COLUMN */}
                      <div className="col-span-1.5 flex justify-end gap-1.5 shrink-0">
                        {!isClientUser(currentUser) && (
                          isEditing ? (
                            <>
                              <button 
                                onClick={() => saveEditItem(item.id)}
                                className="p-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 rounded-lg transition-all cursor-pointer"
                                title="Guardar Cambios"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={cancelEditItem}
                                className="p-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-lg transition-all cursor-pointer"
                                title="Cancelar"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                onClick={() => startEditItem(item)}
                                className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                                title="Editar Mercancía"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={() => deleteItem(item.id)}
                                className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                                title="Eliminar del Catálogo"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )
                        )}
                      </div>

                    </div>
                  );
                })
              )}

            </div>
          </div>
        </div>

        {/* Goods Creation Side Form - With Material Image Upload / Selection */}
        {isAdding && !isClientUser(currentUser) && (
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-md h-fit space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-slate-850" />
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-tight">Nueva Mercancía</h4>
              </div>
              <button onClick={() => setIsAdding(false)} className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer">✕</button>
            </div>

            <form onSubmit={handleAddGoods} className="space-y-4">
              
              {/* IMAGE UPLOAD & PRESET SELECTION - Matches "opción de subir una foto del material con vista de la imagen" */}
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-gray-700 block">Fotografía del Material</label>
                
                {/* Active Image Preview & Custom Upload Trigger */}
                <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200/70">
                  <img 
                    src={gImageUrl} 
                    alt="Preview" 
                    className="w-14 h-14 rounded-lg object-cover border border-slate-300 shadow-xs shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = PRESET_IMAGES[0].url;
                    }}
                  />
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-500 font-medium">Sube una foto personalizada o selecciona un preajuste debajo.</p>
                    <label className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-[10px] font-bold text-slate-700 cursor-pointer shadow-xs transition-all">
                      <Upload className="h-3 w-3 text-slate-500" />
                      Subir Archivo
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => handleImageUpload(e, false)}
                      />
                    </label>
                  </div>
                </div>

                {/* Preset List for Professional Quick-Selection */}
                <div className="space-y-1">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block font-mono">Preajustes Profesionales:</span>
                  <div className="flex flex-wrap gap-1">
                    {PRESET_IMAGES.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setGImageUrl(p.url);
                          setGSelectedPresetId(p.id);
                        }}
                        className={`px-2 py-1 rounded text-[9px] font-bold transition-all border cursor-pointer ${
                          gSelectedPresetId === p.id
                            ? 'bg-slate-900 border-slate-900 text-white shadow-xs'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-gray-700">Código de Mercancía</label>
                <input
                  type="text"
                  required
                  placeholder="PROD-TUB-GALV"
                  value={gCode}
                  onChange={(e) => setGCode(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-slate-400 font-mono uppercase"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-gray-700">Descripción / Detalles Técnicos</label>
                <input
                  type="text"
                  required
                  placeholder="Tubo de Acero Galvanizado..."
                  value={gDesc}
                  onChange={(e) => setGDesc(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-slate-400"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-gray-700">Ficha Técnica / Especificaciones</label>
                <textarea
                  placeholder="Normas, dimensiones, grados, materiales, etc."
                  value={gSpecs}
                  onChange={(e) => setGSpecs(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-slate-400 min-h-[60px] resize-y"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-700">Unidad de Medida</label>
                  <select
                    value={gUnit}
                    onChange={(e) => setGUnit(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-slate-400"
                  >
                    <option value="PIECE">PIECE (Pieza)</option>
                    <option value="PZA">PZA (Pieza)</option>
                    <option value="MTR">MTR (Metro)</option>
                    <option value="KG">KG (Kilogramo)</option>
                    <option value="LTM">LTM (Lote)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-700">Peso Estimado (Kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={gWeight}
                    onChange={(e) => setGWeight(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-hidden focus:border-slate-400 font-mono"
                  />
                </div>
              </div>

              {/* Price input adapts dynamically based on catalogCurrency selection */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-gray-700 block">
                  Precio unitario en <span className="font-extrabold text-slate-900">{catalogCurrency}</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-2.5 text-xs text-gray-400 font-bold font-mono">
                    {catalogCurrency === 'MXN' ? '$' : 'US$'}
                  </div>
                  <input
                    type="number"
                    required
                    min="0"
                    step="any"
                    placeholder="0.00"
                    value={gPriceInput}
                    onChange={(e) => setGPriceInput(e.target.value)}
                    className={`w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-3 py-2 text-xs focus:outline-hidden focus:border-slate-450 font-mono font-bold ${
                      catalogCurrency === 'MXN' ? 'text-emerald-600' : 'text-blue-600'
                    }`}
                  />
                </div>
              </div>

              {/* Automatic currency conversions in side view */}
              <div className="bg-slate-50 p-3 rounded-xl text-[10px] text-slate-600 space-y-1.5 border border-slate-200/60">
                <p className="font-bold uppercase tracking-wider text-[8px] text-slate-500">Conversión de Divisas Automática:</p>
                {catalogCurrency === 'MXN' ? (
                  <p>Equivalente USD: <strong className="font-bold text-slate-900">${formatPriceValue(((parseFloat(gPriceInput) || 0) / exchangeRate))} USD</strong></p>
                ) : (
                  <p>Equivalente MXN: <strong className="font-bold text-slate-900">${formatPriceValue(((parseFloat(gPriceInput) || 0) * exchangeRate))} MXN</strong></p>
                )}
                <p className="text-[8px] text-slate-400">Usando tipo de cambio sincronizado de $1 USD = ${exchangeRate.toFixed(4)} MXN.</p>
              </div>

              <button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Save className="h-4 w-4" /> Guardar en Catálogo
              </button>
            </form>
          </div>
        )}

      </div>

      {/* FULL-SIZE IMAGE ZOOM MODAL */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-slate-900/85 backdrop-blur-xs flex items-center justify-center p-4 z-55 transition-all animate-fade-in"
          onClick={() => setZoomedImage(null)}
        >
          <div 
            className="bg-white rounded-2xl overflow-hidden max-w-2xl w-full shadow-2xl border border-slate-100 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-tight">{zoomedImage.title}</h4>
              <button 
                onClick={() => setZoomedImage(null)}
                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="relative aspect-video max-h-[450px] bg-slate-950 flex items-center justify-center">
              <img 
                src={zoomedImage.url} 
                alt={zoomedImage.title} 
                className="max-h-full max-w-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = PRESET_IMAGES[0].url;
                }}
              />
            </div>
            <div className="p-3 border-t border-slate-100 flex justify-end bg-slate-50 gap-2">
              <button
                onClick={() => {
                  setThreeDItem({ imageUrl: zoomedImage.url, title: zoomedImage.title });
                  setZoomedImage(null);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-[10.5px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
              >
                <Rotate3d className="h-4 w-4 text-cyan-400 animate-pulse" />
                <span>Iniciar Simulación 3D</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3D INTERACTIVE HOLOGRAM VISUALIZER MODAL */}
      {threeDItem && (
        <ThreeDViewer
          imageUrl={threeDItem.imageUrl}
          title={threeDItem.title}
          itemCode={threeDItem.code}
          onClose={() => setThreeDItem(null)}
        />
      )}

    </div>
  );
}
